import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Eye } from "lucide-react";
import { Customer } from "@/types/customer";

interface CustomersListProps {
  customers: Customer[];
  loading: boolean;
  error: string | null;
}

export const CustomersList: React.FC<CustomersListProps> = ({ customers, loading, error }) => {
  if (loading) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Orders</TableHead>
            <TableHead>Spent</TableHead>
            <TableHead>Latest Order</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array(5).fill(0).map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <Skeleton className="h-4 w-[120px]" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-[180px]" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-[100px] rounded-full" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-[30px]" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-[60px]" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-[80px]" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-8 w-[70px] rounded" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  if (error) {
    return (
      <div className="text-center p-4">
        <p className="text-destructive">Error loading customers: {error}</p>
      </div>
    );
  }

  if (customers.length === 0) {
    return (
      <div className="text-center p-4">
        <p className="text-muted-foreground">No customers found</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Orders</TableHead>
          <TableHead>Spent</TableHead>
          <TableHead>Latest Order</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {customers.map((customer) => (
          <TableRow key={customer.id}>
            <TableCell className="font-medium">
              {customer.first_name || customer.last_name
                ? `${customer.first_name || ''} ${customer.last_name || ''}`
                : 'No name'}
            </TableCell>
            <TableCell>{customer.email}</TableCell>
            <TableCell>
              <Badge variant={customer.is_registered ? "default" : "secondary"}>
                {customer.is_registered ? "Registered" : "Not Registered"}
              </Badge>
            </TableCell>
            <TableCell>{customer.total_orders || 0}</TableCell>
            <TableCell>${(customer.total_spend || 0).toFixed(2)}</TableCell>
            <TableCell>
              {customer.latest_order_date
                ? new Date(customer.latest_order_date).toLocaleDateString()
                : 'Never'}
            </TableCell>
            <TableCell className="text-right">
              <Button variant="outline" size="sm" asChild>
                <Link to={`/customers/${customer.id}`}>
                  <Eye className="h-4 w-4 mr-1" /> View
                </Link>
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
